/**
 * A prompt, as the parts it is actually made of.
 *
 * An instruction for this product is not a paragraph. It is a role, a stance, a
 * rule about what may be stated, a list of banned phrasings, a format, and a
 * worked example - and each of those is a different KIND of decision, written at
 * a different time by a different person. One 20,000-character textarea presents
 * them as undifferentiated text, so the parts that matter most get lost between
 * the parts nobody has revisited in months, and adding a section means guessing
 * where it goes and what the house convention for it is.
 *
 * So the editor works in blocks, and this module is the whole of the format:
 *
 *   a block is a `## heading` and the lines under it;
 *   text before the first heading is the opening block, which has no heading;
 *   `###` and everything else is content, untouched.
 *
 * STORAGE DOES NOT CHANGE. `prompt_versions.body` is still plain text, which is
 * still exactly what the model is sent. Blocks are a view: parsed on the way
 * into the editor, composed on the way out, and a prompt written before this
 * existed opens as the blocks it always had. Nothing downstream knows about it -
 * the lint, the preview, the diff and the renderer all read the same string they
 * read before.
 *
 * ROUND TRIP OR NOTHING. compose(parse(text)) gives the instruction back, or an
 * author loses reviewed wording by opening a screen. Exactly, for the wording
 * this build ships - that is what the unit test pins.
 *
 * The one thing it normalises is blank lines directly under a heading, which are
 * invisible to a reader and to a model and would otherwise have to survive a
 * textarea that trims them. So an instruction written elsewhere may come back
 * one blank line shorter the first time it is saved, and is stable from then on.
 * That is a deliberate trade and the alternative - blocks whose bodies start
 * with whitespace nobody can see - is worse to edit.
 */

/** One `## section` of an instruction. */
export interface PromptBlock {
  /** Stable across edits so React keeps the caret where the writer put it. */
  id: string;
  /** '' for the opening block, which is the role and sits above any heading. */
  heading: string;
  body: string;
}

let counter = 0;
const nextId = () => `b${(counter += 1)}`;

/**
 * Split an instruction into its blocks.
 *
 * Only `## ` starts one. `###` is a sub-heading inside a section - the request
 * block ships with three of them - and treating it as a block would shred a
 * section into fragments no author wrote.
 */
export function parseBlocks(text: string): PromptBlock[] {
  const blocks: PromptBlock[] = [];
  let heading = '';
  let lines: string[] = [];

  const flush = () => {
    const body = lines.join('\n').replace(/^\n+|\n+$/g, '');
    // An empty opening block is the normal case for an instruction that starts
    // with a heading; an empty one anywhere else is a section somebody cleared.
    if (heading !== '' || body !== '') blocks.push({ id: nextId(), heading, body });
    lines = [];
  };

  for (const line of text.replace(/\r\n?/g, '\n').split('\n')) {
    const match = /^##\s+(.*)$/.exec(line);
    if (match) {
      flush();
      heading = match[1]!.trim();
      continue;
    }
    lines.push(line);
  }
  flush();

  return blocks;
}

/** The blocks back into the string the model is sent. */
export function composeBlocks(blocks: PromptBlock[]): string {
  return blocks
    .map((block) => {
      const body = block.body.replace(/^\n+|\n+$/g, '');
      if (block.heading.trim() === '') return body;
      return body === '' ? `## ${block.heading.trim()}` : `## ${block.heading.trim()}\n${body}`;
    })
    .filter((part) => part !== '')
    .join('\n\n');
}

/** A block with nothing in it yet, for 自由記述. */
export const emptyBlock = (heading = ''): PromptBlock => ({ id: nextId(), heading, body: '' });

/**
 * What each section of the shipped instruction is for.
 *
 * Keyed by heading, because that is what an author sees. A heading not in here
 * gets no hint rather than a generic one - "this is a section of the prompt"
 * teaches nobody anything and trains people to stop reading the hints that do
 * say something.
 */
export const BLOCK_HINTS: Record<string, string> = {
  '書き手としての姿勢': '誰として書くか。具体を優先する・断定しない、といった書き方の構え。',
  /*
   * The two categories, and the two rules that stand on them.
   *
   * These three headings replaced 「この記事にしか書けないことを書く」 and 「事実の扱い」.
   * The old pair read as one rule - "only approved facts" - which banned the
   * treatment explanation along with the invented price, and left generation
   * with nothing to write but hedges. The hints say which half each block
   * governs, because an operator editing one of them without knowing that is
   * how the ban gets rebuilt by accident.
   */
  '書いてよいことの範囲':
    '(A) 施術や体のしくみの一般知識と、(B) この院の事実を分ける定義。以降の規則がこの2つを参照します。ここを消すと、記事が一般論だけ・院の情報だけのどちらかに寄ります。',
  '具体的に書く（最重要）':
    '空っぽな文を消す2つのテスト。(A) は施術名を入れ替えても成り立つ文を禁じ、(B) は他院でも成り立つ文を禁じます。いちばん効く指示。',
  '密度（見出しひとつにつき満たすこと）':
    '1つの見出しにどれだけ具体を入れるか。数値で言うほど守られます。',
  '単調さを避ける': '同じ書き出し・同じ段落の長さを繰り返さないための規則。AIらしさが出る主因。',
  '表現の禁止事項（医療広告ガイドライン・薬機法・景品表示法）':
    '書いてはいけない表現の一覧。法令側の要請なので、消さずに足す形で運用します。',
  '日本語': '文の長さ、語尾、敬体。読みやすさの土台。',
  '出力': 'Markdownの使い方、見出しレベル、前置きを書かないこと。',
  '書き方の見本（形式の見本です。ここに出てくる数値・施術名は事実として使いません）':
    '悪い例と良い例。文体は説明より実例のほうが伝わります。',
  '依頼': 'この生成で何を作ってほしいか。依頼文の本体。',
  /*
   * The per-use halves. Each of these appears in one brief only - the job it
   * describes - so a hint that explained them generically would be wrong on
   * every screen but one.
   */
  'この生成の仕事':
    'この生成が何をするか。構成案なら「本文は書かない」、リライトなら「対象だけを書き直す」。ここを消すと、その生成が別の仕事を始めます。',
  '良い見出しの条件':
    '見出しの良し悪しの基準。記事がどこまで具体的になれるかは、ほぼここで決まります（入れ替え可能な見出し10本は、入れ替え可能な節10本になります）。',
  '記事全体の組み立て': '導入・本編・まとめ・CTAの並びと、見出しを足さない規則。記事全体を1回で書く生成だけが使います。',
  '密度（この節で満たすこと）': '1つの節にどれだけ具体を入れるか。数値で言うほど守られます。',
  '密度（各H2で満たすこと）': '1つの見出しにどれだけ具体を入れるか。記事全体を1回で書く生成向け。',
  /*
   * The six shape overrides. Each REPLACES the built-in format for that kind
   * entirely - the literal syntax included - which is why the hint says so
   * plainly: this is not a policy tweak layered on the default, it is the
   * whole instruction for that kind from here on.
   */
  '節の書き方: summary':
    '冒頭の早見表の書き方。ここを書き換えると、既定の4行表の代わりにこの内容が使われます。',
  '節の書き方: checklist':
    'チェックリストの書き方。項目数やHTML形式を含め、既定の代わりにこの内容が使われます。',
  '節の書き方: steps': '施術の流れの書き方。ステップ数や書式を含め、既定の代わりにこの内容が使われます。',
  '節の書き方: reasons':
    '選ばれる理由の書き方。{{strengths}}で登録済みの強みを渡せます。既定の代わりにこの内容が使われます。',
  '節の書き方: faq': 'よくある質問の書き方。既定の代わりにこの内容が使われます。',
  '節の書き方: body': '通常の本文の書き方。既定の代わりにこの内容が使われます。',
};

/**
 * Sections of the shipped instruction that this draft does not have.
 *
 * Offered as things to add, with the shipped wording as their starting content -
 * so "add the banned-expressions section" produces the reviewed list rather than
 * an empty box and a memory test. Derived from the default the server sends
 * rather than copied into this file: one wording, in one place, and a change to
 * it turns up here without anybody syncing anything.
 */
/**
 * The shipped opening paragraph - the role and reader, above any heading -
 * offered back when the current draft has none.
 *
 * Kept apart from missingBlocks because an opening block has no heading to
 * match against and is positional: it has to land at index 0, or a re-parse
 * would glue its text onto whatever section happens to precede it. A blank
 * draft has never seen this text at all, which is why it is offered rather than
 * silently prefilled - see PromptComposer for where it is inserted.
 */
export function missingOpening(current: PromptBlock[], shipped: string): PromptBlock | null {
  const already = current[0]?.heading === '' && current[0]?.body.trim() !== '';
  if (already) return null;
  const first = parseBlocks(shipped)[0];
  if (!first || first.heading !== '' || first.body.trim() === '') return null;
  return { ...first, id: nextId() };
}

export function missingBlocks(current: PromptBlock[], shipped: string): PromptBlock[] {
  const have = new Set(current.map((block) => block.heading.trim()));
  return parseBlocks(shipped).filter(
    (block) => block.heading.trim() !== '' && !have.has(block.heading.trim()),
  );
}
